import Dataset from "datasets/types/interfaces/dataset";
import { mutate } from "swr";
import useToast from "~/components/ui/toast";
import { useAsyncMutation } from "~/pages/manifold-nextgen/hooks/useAsyncMutation";
import { Agent } from "~/services/agents";
import {
  projectAgentsPath,
  projectDatasetsPath,
  setProjectAgents,
  setProjectDatasets,
} from "~/services/projects";

interface UseProjectEntityRemovalReturn {
  handleRemoveDataset: (datasetId: string) => void;
  handleRemoveAgent: (agentId: string) => void;
  isRemovingDataset: boolean;
  isRemovingAgent: boolean;
}

// Shared by the project context sidebar TOC and the Settings page's
// Datasets/Agents sections — the two places an owner detaches an entity from
// a project — so the mutation, revalidation, and success toast stay in sync.
export const useProjectEntityRemoval = (
  projectId: string,
  attachedDatasets: Dataset[] | undefined,
  attachedAgents: Agent[] | undefined
): UseProjectEntityRemovalReturn => {
  const { showSuccess } = useToast();

  const { execute: removeDataset, isPending: isRemovingDataset } = useAsyncMutation(
    (ids: string[]) => setProjectDatasets(projectId, ids),
    {
      errorMessage: "Failed to remove dataset",
      onSuccess: async () => {
        await mutate(projectDatasetsPath(projectId));
        showSuccess("Dataset removed");
      },
    }
  );

  const { execute: removeAgent, isPending: isRemovingAgent } = useAsyncMutation(
    (ids: string[]) => setProjectAgents(projectId, ids),
    {
      errorMessage: "Failed to remove agent",
      onSuccess: async () => {
        await mutate(projectAgentsPath(projectId));
        showSuccess("Agent removed");
      },
    }
  );

  const handleRemoveDataset = (datasetId: string) => {
    const remaining = (attachedDatasets ?? []).filter((d) => d.id !== datasetId).map((d) => d.id);
    void removeDataset(remaining);
  };

  const handleRemoveAgent = (agentId: string) => {
    const remaining = (attachedAgents ?? []).filter((a) => a.id !== agentId).map((a) => a.id);
    void removeAgent(remaining);
  };

  return { handleRemoveDataset, handleRemoveAgent, isRemovingDataset, isRemovingAgent };
};
